// pms-core / domain — « Surclassements de demain », the day-before review.
//
// docs/PMS-TETRIS-BRIEF.md §6, the operator's scheme (2026-10-01): allocate by
// the booked category and the party; the day before arrival, if many superior
// rooms are still free, upgrade — half board and B&B before room only, longer
// stays first. An upgrade never changes what the guest bought (§6.2), so it can
// always be undone.
//
// A FUNCTIONAL CORE: it proposes, never assigns. Each proposal is checked
// against the ones before it, so the list can be applied top to bottom.
//
// What « many » means is a setting, `keepFree` (operator Q10: 2): an upgrade is
// proposed only while at least that many SUPERIOR rooms (DSUP, TSUP, FAMS
// pooled) stay free on every night of the stay, so the hotel keeps rooms to
// sell to a late SUP booking.
//
// The bed rule is NOT lifted here: an upgrade is a gift, and « you cannot
// upgrade a 2-single bed in a DSUP » (operator). Only an overbooking bends it,
// and that is `planRoomReshuffle`'s business.

import { pmsAddDays, pmsNightCount, type PmsBoard } from './pms.js';
import type { AssignableRoom } from './roomAssignment.js';
import { bedStandsFor, categoryDominates, roomMeetsBed, soldCategory, type RoomFit } from './roomFit.js';
import type { PlannerStay } from './roomReshuffle.js';
import type { IsoDate, RoomId } from './types.js';

/** Superior rooms that must stay free each night (operator Q10). */
export const UPGRADE_KEEP_FREE = 2;

export interface UpgradeOptions {
  keepFree?: number;
  /** « Ne pas déplacer » — the desk's veto. */
  excluded?: ReadonlySet<string>;
  /** Whether every night of a stay was read. Default: all were. */
  isFullyRead?: (s: PlannerStay) => boolean;
  boardOf?: (ratePlanId: number) => PmsBoard | null;
}

export interface UpgradeProposal {
  stayId: string;
  /** null for a stay still to allocate: its first room is the upgrade. */
  fromRoomId: RoomId | null;
  toRoomId: RoomId;
  board: PmsBoard | null;
  nights: number;
  /** The superior room already stands made up as asked (null: no request). */
  bedReady: boolean | null;
  /** Superior rooms still free on the stay's tightest night once this one is
   *  applied — what the desk is giving away. */
  superiorFreeAfter: number;
}

const BOARD_RANK: Record<string, number> = { hb: 0, bb: 1, none: 2 };

const overlaps = (a: { checkinDate: string; checkoutDate: string }, b: { checkinDate: string; checkoutDate: string }) =>
  a.checkinDate < b.checkoutDate && b.checkinDate < a.checkoutDate;

/**
 * The upgrades to make for the guests arriving on `arrivalIso` (tomorrow, at the
 * desk's review), best first.
 *
 * `stays` is every stay holding a unit around those dates; unassigned ones are
 * candidates too, since their first room may as well be the better one.
 */
export function proposeUpgrades(
  arrivalIso: IsoDate,
  rooms: readonly AssignableRoom[],
  stays: readonly PlannerStay[],
  fit: RoomFit,
  options: UpgradeOptions = {},
): UpgradeProposal[] {
  const keepFree = options.keepFree ?? UPGRADE_KEEP_FREE;
  const superior = (r: AssignableRoom) =>
    r.roomTypeId !== null && (fit.categories.get(r.roomTypeId)?.superior ?? false);
  const roomById = new Map(rooms.map((r) => [r.id, r]));
  const supRooms = rooms.filter(superior);

  // Where everyone is, updated as upgrades are proposed.
  const where = new Map<string, RoomId>();
  for (const s of stays) if (s.roomId !== null && roomById.has(s.roomId)) where.set(s.id, s.roomId);
  const byId = new Map(stays.map((s) => [s.id, s]));
  const holds = (roomId: RoomId, span: { checkinDate: string; checkoutDate: string }, except?: string) =>
    [...where.entries()].some(([id, r]) => r === roomId && id !== except && overlaps(byId.get(id)!, span));

  const board = (s: PlannerStay) => options.boardOf?.(s.ratePlanId) ?? null;
  const nights = (s: PlannerStay) => pmsNightCount(s.checkinDate, s.checkoutDate);

  const candidates = stays
    .filter((s) => s.checkinDate === arrivalIso)
    .filter((s) => s.status === 'confirmed' || s.status === 'reserved')
    .filter((s) => !(options.excluded?.has(s.id) ?? false))
    .filter((s) => options.isFullyRead === undefined || options.isFullyRead(s))
    .filter((s) => {
      const home = s.roomId === null ? undefined : roomById.get(s.roomId);
      return home === undefined || !superior(home);
    })
    .sort(
      (a, b) =>
        (BOARD_RANK[board(a) ?? 'none'] ?? 2) - (BOARD_RANK[board(b) ?? 'none'] ?? 2) ||
        nights(b) - nights(a) ||
        a.id.localeCompare(b.id),
    );

  /** Superior rooms free on the stay's tightest night, `who` placed in `roomId`. */
  const freeAfter = (who: PlannerStay, roomId: RoomId): number => {
    let tightest = Number.POSITIVE_INFINITY;
    for (let d = who.checkinDate; d < who.checkoutDate; d = pmsAddDays(d, 1)) {
      const night = { checkinDate: d, checkoutDate: pmsAddDays(d, 1) };
      const free = supRooms.filter((r) => r.id !== roomId && !holds(r.id, night, who.id)).length;
      tightest = Math.min(tightest, free);
    }
    return tightest;
  };

  const out: UpgradeProposal[] = [];
  for (const s of candidates) {
    const options_ = supRooms
      .filter((r) => categoryDominates(fit, r.roomTypeId, soldCategory(s)))
      .filter((r) => r.maxOccupancy === null || r.maxOccupancy >= s.adults + s.children)
      .filter((r) => roomMeetsBed(fit, r, s.requestedBedConfig))
      .filter((r) => !holds(r.id, s, s.id))
      .map((r) => ({ r, after: freeAfter(s, r.id), ready: bedStandsFor(fit, r, s.requestedBedConfig) }))
      .filter((o) => o.after >= keepFree)
      .sort(
        (a, b) =>
          (a.ready === true ? 0 : a.ready === null ? 1 : 2) - (b.ready === true ? 0 : b.ready === null ? 1 : 2) ||
          (fit.categories.get(a.r.roomTypeId!)?.size ?? 0) - (fit.categories.get(b.r.roomTypeId!)?.size ?? 0) ||
          a.r.roomNumber.localeCompare(b.r.roomNumber, 'fr', { numeric: true }),
      );
    const best = options_[0];
    if (best === undefined) continue;
    out.push({
      stayId: s.id,
      fromRoomId: s.roomId,
      toRoomId: best.r.id,
      board: board(s),
      nights: nights(s),
      bedReady: best.ready,
      superiorFreeAfter: best.after,
    });
    where.set(s.id, best.r.id);
  }
  return out;
}
